'use client';

import { useRef } from 'react';
import NextImage from 'next/image';
import { useGuideVerticalProgress } from './useGuideVerticalProgress';
import type { ReactNode } from 'react';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import CmsImage from '@/components/ui/CmsImage';
import { cn } from '@/components/ui/utils';
import { getImageUrlForVariant } from '@/lib/homepage-mappers';
import { getStrapiOriginalMediaUrl } from '@/lib/strapi-media-url';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { HomeBuildingGuideItem } from '../../data/guides';
import type { GuideInnerAccordionTextBlock, TransformedGuideInnerData } from '@/types/guide-inner';
import CmsRichTextHeading from '@/components/ui/CmsRichTextHeading';

type NullableString = string | null | undefined;

interface PlanningGuidePageProps {
  locale: string;
  guide: HomeBuildingGuideItem;
  cmsData?: TransformedGuideInnerData | null;
  /**
   * Streaming slot for the FAQ + enquiry block — rendered by an async
   * Server Component wrapped in `<Suspense>` in `page.tsx`.
   */
  faqSlot?: ReactNode;
}

type MaterialBlock = {
  title: string;
  subtitle: string;
  description: string;
  image: string;
};

type ProductCard = {
  id: string | number;
  name: string;
  description: string;
  image: string;
};

const HARDCODED_MATERIAL_BLOCKS: MaterialBlock[] = [
  {
    title: 'Cement & Concrete',
    subtitle: 'The Binding Force',
    description:
      'When it comes to selecting the materials required for house construction, cement is the most critical element of your build. It is the glue that holds your home together. For high-strength structures, choose cement from a trusted manufacturer with low moisture and low heat of hydration to prevent cracks.',
    image: '/images/guid_gr_fl1.jpg',
  },
  {
    title: 'Steel (TMT Bars)',
    subtitle: 'The Backbone',
    description:
      'Steel provides the tension strength that concrete lacks. Without high-quality steel, your home cannot withstand seismic shifts or heavy loads. Choose TMT (Thermo-Mechanically Treated) bars with a high ductility rating. Ensure they are free from thick layers of rust before they are cast into concrete.',
    image: '/images/guid_gr_fl2.png',
  },
  {
    title: 'Sand and Aggregates',
    subtitle: 'The Fillers',
    description:
      'Often overlooked, the bulk of your concrete consists of sand and crushed stone. Excessive fine sand or river silt can weaken the bond in the concrete. If using river sand, it must be washed. If using M-Sand (Manufactured Sand), ensure it is sourced from a reputable crusher with a consistent grain size.',
    image: '/images/guid_gr_fl3.png',
  },
  {
    title: 'Bricks and Blocks',
    subtitle: 'The Enclosure',
    description:
      'Your choice of wall material impacts the temperature and sound insulation of your home. Red bricks are traditional and sturdy, but AAC (Autoclaved Aerated Concrete) blocks are lighter, offer better thermal insulation, and reduce the overall load on your building’s structure.',
    image: '/images/guild_351.jpg',
  },
];

const HARDCODED_PRODUCTS: ProductCard[] = [
  {
    id: 'wonder-plus',
    name: 'Wonder Plus',
    description: 'For foundations, beams, and load-caring columns.',
    image: '/images/guid_gr.jpg',
  },
  {
    id: 'wonder-ppc',
    name: 'Wonder PPC',
    description: 'For masonry, brickwork, and all plastering requirements.',
    image: '/images/guid_gr1.jpg',
  },
  {
    id: 'wonder-xtreme',
    name: 'Wonder Xtreme',
    description: 'For roof slabs and high-strength structural work.',
    image: '/images/guid_349.png',
  },
  {
    id: 'wonder-opc',
    name: 'Wonder OPC',
    description: 'For high-grade concrete mixes and heavy-load constructions.',
    image: '/images/guid_350.png',
  },
];

function stripHtml(html: NullableString): string {
  if (!html) return '';
  return html
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&apos;/gi, "'")
    .replace(/&rsquo;/gi, '’')
    .trim();
}

/**
 * One row of the vertical-progress flow on the Planning page. Either an
 * accordion-style material block (`type: 'material'`) or the
 * "Our Specialised Products" embed (`type: 'products'`) — order is whatever
 * the CMS author put in the `accordionBlocks` array.
 */
type PlanningRow =
  | { kind: 'material'; key: string; data: MaterialBlock }
  | {
      kind: 'products';
      key: string;
      heading: string;
      subHeading: string;
      cards: ProductCard[];
    };

function buildRowsFromCms(blocks: GuideInnerAccordionTextBlock[]): PlanningRow[] {
  return blocks.map((b): PlanningRow => {
    if (b.otherItemsBlock) {
      return {
        kind: 'products',
        key: `cms-products-${b.id}`,
        heading: b.heading ?? 'Our Specialised Products for Your Home',
        subHeading: stripHtml(b.description),
        cards: (b.otherItems ?? []).map((item, idx) => ({
          id: item.id ?? idx,
          name: item.heading,
          description: stripHtml(item.description),
          image:
            getImageUrlForVariant(item.media, 'large') ??
            HARDCODED_PRODUCTS[idx % HARDCODED_PRODUCTS.length].image,
        })),
      };
    }
    return {
      kind: 'material',
      key: `cms-material-${b.id}`,
      data: {
        title: b.heading ?? '',
        subtitle: b.smallHeading ?? '',
        description: stripHtml(b.description),
        image: getImageUrlForVariant(b.image, 'large') ?? '/images/guid_gr_fl1.jpg',
      },
    };
  });
}

function MaterialRow({ row }: { row: Extract<PlanningRow, { kind: 'material' }> }) {
  const block = row.data;
  return (
    <article className="overflow-hidden rounded-xl bg-white lg:h-[430px]">
      <div className="grid h-full grid-cols-1 items-center lg:grid-cols-[1fr_0.92fr]">
        <div className="px-5 py-6 sm:px-6 sm:py-7 lg:px-7 lg:py-7">
          <h3 className="text-2xl font-semibold leading-tight text-[#111827] sm:text-3xl lg:text-[40px]">
            {block.title}
          </h3>
          {block.subtitle ? (
            <p className="mt-1 text-lg font-medium leading-tight text-[#111827] sm:text-xl lg:text-[24px]">
              {block.subtitle}
            </p>
          ) : null}
          <p className="mt-3 max-w-[560px] text-sm leading-relaxed text-[#4b5563] md:text-base">
            {block.description}
          </p>
        </div>

        <div className="relative min-h-[220px] sm:min-h-[260px] lg:h-full">
          <NextImage
            src={block.image}
            alt={block.title}
            fill
            sizes="(max-width: 768px) 100vw, 45vw"
            className="object-cover object-center"
          />
        </div>
      </div>
    </article>
  );
}

function ProductsRow({ row }: { row: Extract<PlanningRow, { kind: 'products' }> }) {
  return (
    <section className="rounded-xl bg-black">
      <div className="px-5 py-6 sm:px-6 sm:py-7">
        <div>
          <h3 className="text-2xl font-semibold leading-tight text-white sm:text-3xl lg:text-[40px]">
            {row.heading}
          </h3>
          {row.subHeading ? (
            <p className="mt-1 text-sm leading-relaxed text-white/70 md:text-base">
              {row.subHeading}
            </p>
          ) : null}
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-3">
          {row.cards.map((product) => (
            <article key={product.id}>
              <div className="relative overflow-hidden rounded-lg">
                <div className="relative aspect-[1/0.85]">
                  <NextImage
                    src={product.image}
                    alt={product.name}
                    fill
                    sizes="(max-width: 768px) 50vw, 20vw"
                    className="object-cover object-center"
                  />
                </div>
              </div>
              <p className="mt-2 text-sm font-semibold leading-tight text-white sm:text-base lg:text-[30px] lg:leading-tight">
                {product.name}
              </p>
              <p className="mt-1 text-[11px] leading-relaxed text-white/75 lg:text-xs">
                {product.description}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function buildHardcodedRows(): PlanningRow[] {
  // Match the original visual: products grid shown after the first material
  // block, then the rest of the materials.
  const [first, ...rest] = HARDCODED_MATERIAL_BLOCKS;
  const rows: PlanningRow[] = [
    { kind: 'material', key: 'static-cement', data: first },
    {
      kind: 'products',
      key: 'static-products',
      heading: 'Our Specialised Products for Your Home',
      subHeading: 'We match our expertise to your specific construction requirements.',
      cards: HARDCODED_PRODUCTS,
    },
  ];
  rest.forEach((block, idx) =>
    rows.push({ kind: 'material', key: `static-material-${idx}`, data: block }),
  );
  return rows;
}

const DEFAULT_PLANNING_CHECKLIST_ITEMS = [
  {
    id: 1,
    heading: 'Cement Freshness',
    description:
      'Check the manufacturing date; cement should ideally be used within 90 days of packing.',
  },
  {
    id: 2,
    heading: 'Steel Integrity',
    description: 'TMT bars should be bendable without showing surface cracks.',
  },
  {
    id: 3,
    heading: 'Sand Texture',
    description:
      'Rub a sample of sand between damp palms; it should not leave a layer of clay or silt on your skin.',
  },
  {
    id: 4,
    heading: 'Brick Strength',
    description:
      'A good brick should not break when dropped from a height of 4 feet on level ground.',
  },
  {
    id: 5,
    heading: 'Water Quality',
    description:
      "The water used for mixing concrete should be clean and potable. If you wouldn't drink it, don't build with it.",
  },
];

function PlanningChecklistSection({
  checklist,
  hidden,
}: {
  checklist: TransformedGuideInnerData['checklist'] | undefined;
  hidden: boolean;
}) {
  const items = checklist?.data ?? DEFAULT_PLANNING_CHECKLIST_ITEMS;
  return (
    <section className={`${hidden ? 'hidden' : ''} bg-[#efefef] py-12 md:py-16`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-0">
        <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[1fr_1fr] lg:gap-14">
          <div className="max-w-[560px]">
            <h3 className="text-3xl font-semibold leading-[1.08] text-[#111827] sm:text-4xl lg:text-4xl">
              {checklist?.heading ?? 'Home Construction Material Checklist'}
            </h3>
            {checklist?.subHeading ? (
              <div
                className="mt-4 max-w-md text-sm leading-relaxed text-[#4b5563] md:text-base [&_p]:m-0"
                dangerouslySetInnerHTML={{ __html: checklist.subHeading }}
              />
            ) : (
              <p className="mt-4 max-w-md text-sm leading-relaxed text-[#4b5563] md:text-base">
                Now that you have read our building material selection guide, use this checklist to
                verify your materials as they arrive on-site.
              </p>
            )}
          </div>

          <div className="rounded-xl bg-[#f7f7f7] px-5 py-5 sm:px-6 sm:py-6 lg:px-7 lg:py-7">
            <ul className="space-y-4">
              {items.map((item) => (
                <li key={item.id} className="flex items-start gap-3">
                  <NextImage
                    src="/images/checkgrn.svg"
                    alt=""
                    aria-hidden
                    width={16}
                    height={16}
                    className="mt-1 shrink-0"
                  />
                  <div>
                    <p className="text-sm font-semibold leading-tight text-[#111827] md:text-base">
                      {item.heading}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-[#6b7280] md:text-sm">
                      {stripHtml(item.description)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function PlanningToolkitSection({
  toolkit,
  toolkitButtons,
  hidden,
  locale,
  resolveButtonHref,
}: {
  toolkit: TransformedGuideInnerData['toolkit'] | undefined;
  toolkitButtons: NonNullable<TransformedGuideInnerData['toolkit']>['buttonWhite'];
  hidden: boolean;
  locale: string;
  resolveButtonHref: (buttonUrl: NullableString, fallback: string) => string;
}) {
  return (
    <section className={`${hidden ? 'hidden' : ''} bg-[#efefef] pb-12 md:pb-16`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-0">
        <div className="relative overflow-hidden rounded-2xl bg-[#3a0004] px-5 py-6 sm:px-6 lg:px-10 max-sm:!pb-0">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-[1fr_0.95fr]">
            <div className="relative z-10 max-w-[520px] text-white lg:py-14">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">
                {toolkit?.heading ?? 'Your Precision Toolkit'}
              </p>
              {toolkit?.subHeading ? (
                <div
                  className="mt-3 max-w-md text-xl font-semibold leading-tight sm:text-2xl [&_p]:m-0"
                  dangerouslySetInnerHTML={{ __html: toolkit.subHeading }}
                />
              ) : (
                <h3 className="mt-3 max-w-md text-xl font-semibold leading-tight sm:text-2xl">
                  Ensure zero wastage by using our specialised tools to plan your material
                  requirements with accuracy.
                </h3>
              )}

              <div className="mt-6 flex flex-wrap items-center gap-3">
                {toolkitButtons.length > 0 ? (
                  toolkitButtons.map((btn, idx) => {
                    const isOutlined = btn.buttonType === 'Transparent';
                    const href = resolveButtonHref(btn.buttonUrl, `/${locale}/tools`);
                    return (
                      <SwapTextButton
                        key={btn.id ?? idx}
                        text={btn.buttonText}
                        hoverText={btn.buttonText}
                        href={href}
                        {...getCmsButtonLinkProps(btn)}
                        tone={isOutlined ? undefined : 'light'}
                        variant={isOutlined ? 'outline' : undefined}
                        showArrow={false}
                        className={cn(
                          'min-h-[40px] w-full justify-center rounded-md px-4 py-2 text-xs font-semibold shadow-none sm:w-auto',
                          isOutlined &&
                            '!border-white !bg-transparent !text-white hover:!bg-white hover:!text-[#111827]',
                        )}
                        aria-label={btn.ariaLabel ?? btn.buttonText}
                      />
                    );
                  })
                ) : (
                  <>
                    <SwapTextButton
                      text="Find a Dealer Near You"
                      hoverText="Find a Dealer Near You"
                      href={`/${locale}/tools/dealer-locator`}
                      tone="light"
                      showArrow={false}
                      className="min-h-[40px] w-full justify-center rounded-md px-4 py-2 text-xs font-semibold shadow-none sm:w-auto"
                      aria-label="Find a dealer near you"
                    />
                    <SwapTextButton
                      text="Estimate Construction Cost"
                      hoverText="Estimate Construction Cost"
                      href={`/${locale}/tools/construction-cost-calculator`}
                      variant="outline"
                      showArrow={false}
                      className="min-h-[40px] w-full justify-center rounded-md !border-white !bg-transparent px-4 py-2 text-xs font-semibold !text-white shadow-none hover:!bg-white hover:!text-[#111827] sm:w-auto"
                      aria-label="Estimate construction cost"
                    />
                  </>
                )}
              </div>
            </div>

            <div className="relative min-h-[220px] sm:min-h-[260px] lg:min-h-[320px]">
              <NextImage
                src={
                  getImageUrlForVariant(toolkit?.image ?? null, 'large') ?? '/images/guid_350.png'
                }
                alt={toolkit?.heading ?? 'Planning support toolkit'}
                fill
                sizes="(max-width: 768px) 100vw, 45vw"
                className="object-cover object-right"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Resolve the hero block props (CMS values with hardcoded fallbacks). */
function resolvePlanningHero(
  cmsData: TransformedGuideInnerData | null | undefined,
  guide: HomeBuildingGuideItem,
) {
  const heroMedia = cmsData?.hero.bannerMedia ?? null;
  return {
    heroTitle: cmsData?.hero.heading || 'Selecting the Right House Construction Materials',
    heroTitleRichText: cmsData?.hero.titleRichText ?? null,
    heroSubtitle:
      stripHtml(cmsData?.hero.subheadingHtml) ||
      'Structural integrity depends on the quality of your raw materials.',
    heroDescription:
      stripHtml(cmsData?.hero.descriptionHtml) ||
      'Quality control at this stage is your primary defence against long-term structural issues like dampness, seepage, and wall cracks. Investing in the right materials for home construction not only ensures safety and significantly reduces future maintenance costs.',
    heroMedia,
    heroImage: getStrapiOriginalMediaUrl(heroMedia) ?? '/images/guikd_424.png',
    heroAlt: heroMedia?.alternativeText || guide.title,
    heroWidth: heroMedia?.width || 1440,
    heroHeight: heroMedia?.height || 810,
  };
}

export default function PlanningGuidePage({
  locale,
  guide,
  cmsData,
  faqSlot,
}: PlanningGuidePageProps) {
  const budgetFlowSectionRef = useRef<HTMLElement | null>(null);
  const budgetFlowContentRef = useRef<HTMLDivElement | null>(null);
  const progressColumnRef = useRef<HTMLDivElement | null>(null);
  const progressPinRef = useRef<HTMLDivElement | null>(null);
  const progressRailRef = useRef<HTMLDivElement | null>(null);
  const progressFillRef = useRef<HTMLDivElement | null>(null);
  const progressDotRef = useRef<HTMLSpanElement | null>(null);

  const {
    heroTitle,
    heroTitleRichText,
    heroSubtitle,
    heroDescription,
    heroMedia,
    heroImage,
    heroAlt,
    heroWidth,
    heroHeight,
  } = resolvePlanningHero(cmsData, guide);

  const cmsRows = cmsData?.accordionBlocks?.length
    ? buildRowsFromCms(cmsData.accordionBlocks)
    : null;
  const rows: PlanningRow[] = cmsRows ?? buildHardcodedRows();

  const checklist = cmsData?.checklist;
  const toolkit = cmsData?.toolkit;
  const toolkitButtons = [...(toolkit?.buttonWhite ?? []), ...(toolkit?.buttonDark ?? [])];

  const resolveButtonHref = (buttonUrl: NullableString, fallback: string) => {
    if (!buttonUrl) return fallback;
    return buttonUrl.startsWith('http') ? buttonUrl : `/${locale}${buttonUrl}`;
  };

  useGuideVerticalProgress(
    {
      sectionRef: budgetFlowSectionRef,
      contentRef: budgetFlowContentRef,
      progressColumnRef,
      progressPinRef,
      progressRailRef,
      progressFillRef,
      progressDotRef,
    },
    rows.length,
  );

  return (
    <>
      <section className="overflow-hidden bg-black font-figtree">
        <div className="lg:hidden">
          <CmsImage
            src={heroImage}
            media={heroMedia}
            alt={heroAlt}
            width={heroWidth}
            height={heroHeight}
            priority
            loading="eager"
            sizes="100vw"
            className="h-auto w-full"
            posterSrc={MEDIA_PLACEHOLDER}
            fallbackSrc={MEDIA_PLACEHOLDER}
          />

          <div className="container mx-auto px-4 py-8 sm:px-6 sm:py-10 md:py-12">
            <div className="grid gap-6">
              <div className="max-w-[620px]">
                <CmsRichTextHeading
                  className="hero-title text-white"
                  html={heroTitleRichText}
                  fallback={heroTitle}
                />
              </div>

              <div className="max-w-[420px]">
                <h2 className="text-2xl font-medium leading-tight text-white sm:text-3xl md:text-[2rem]">
                  {heroSubtitle}
                </h2>
                <p className="hero-subtitle mt-4 text-white/85">{heroDescription}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative hidden h-[100svh] overflow-hidden bg-black lg:block">
          <CmsImage
            src={heroImage}
            media={heroMedia}
            alt={heroAlt}
            fill
            priority
            loading="eager"
            sizes="100vw"
            className="object-cover object-center"
            posterSrc={MEDIA_PLACEHOLDER}
            fallbackSrc={MEDIA_PLACEHOLDER}
          />

          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/35 to-black/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/30" />

          <div className="relative z-10 h-full">
            <div className="container mx-auto h-full">
              <div className="grid h-full w-full grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(240px,260px)] xl:grid-cols-[minmax(0,1fr)_minmax(300px,450px)]">
                <div className="max-w-[430px] self-start pt-[148px] xl:max-w-[530px] xl:pt-[168px]">
                  <CmsRichTextHeading
                    className="hero-title text-balance text-white"
                    html={heroTitleRichText}
                    fallback={heroTitle}
                  />
                </div>

                <div className="max-w-[350px] self-end pb-[72px] lg:justify-self-end xl:max-w-[400px]">
                  <h2 className="text-[18px] font-semibold leading-[1.35] tracking-[-0.015em] text-white xl:text-[24px]">
                    {heroSubtitle}
                  </h2>
                  <p className="mt-2 text-[14px] leading-[1.55] tracking-[-0.01em] text-white/85 xl:mt-3 xl:text-[16px]">
                    {heroDescription}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        ref={budgetFlowSectionRef}
        className={`${cmsData && cmsData.accordionBlocks.length === 0 ? 'hidden' : ''} bg-black py-10 md:py-14`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-0">
          <div className="relative grid grid-cols-1 gap-6 lg:grid-cols-[26px_1fr] lg:gap-8">
            <div ref={progressColumnRef} className="relative hidden w-[26px] shrink-0 lg:block">
              <div ref={progressPinRef} className="relative h-full w-full">
                <div
                  ref={progressRailRef}
                  className="absolute bottom-0 left-1/2 top-0 w-px -translate-x-1/2 bg-white/25"
                  aria-hidden
                />
                <div
                  ref={progressFillRef}
                  className="absolute left-1/2 top-0 w-px -translate-x-1/2 bg-[#ed1c25]"
                />
                <span
                  ref={progressDotRef}
                  className="absolute left-1/2 top-0 z-[1] size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#ed1c25]"
                />
              </div>
            </div>

            <div ref={budgetFlowContentRef} className="space-y-5 sm:space-y-6 lg:space-y-8">
              {rows.map((row) =>
                row.kind === 'material' ? (
                  <MaterialRow key={row.key} row={row} />
                ) : (
                  <ProductsRow key={row.key} row={row} />
                ),
              )}
            </div>
          </div>
        </div>
      </section>

      <PlanningChecklistSection checklist={checklist} hidden={Boolean(cmsData && !checklist)} />

      <PlanningToolkitSection
        toolkit={toolkit}
        toolkitButtons={toolkitButtons}
        hidden={Boolean(cmsData && !toolkit)}
        locale={locale}
        resolveButtonHref={resolveButtonHref}
      />

      {/* FAQ + Enquiry Form — streamed via Suspense from page.tsx so the
          lead-forms fetch doesn't block above-the-fold paint. */}
      {faqSlot}
    </>
  );
}
